import {
  lazy,
  Suspense,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Link } from '@tanstack/react-router';
import { addDays, leerDiario, POINTS } from '@selflevel/domain';
import {
  useAreaMap,
  useDayPlan,
  useGoalMilestones,
  useGoals,
  useIdentities,
  useJournal,
  useMilestoneUnlocks,
  useMinutosLocales,
  useMood,
  useProgression,
  useRitualStreak,
  useSettings,
  useTasksOn,
} from '../store/hooks';
import { addTask, deleteTask, saveDayPlan, setTaskDone, updateTask } from '../store/mutations';
import type { DayPlan, LifeArea, Task } from '../store/db';
import { Bar, Card, FilaTarea, Segmentos, fechaLarga, useReciente } from '../ui/primitivas';
import { IconCheck } from '../ui/iconos';
import { nombreDelDia } from '../ui/tira';
import { puedeSerDificil, ritualDestacado, tituloDeTarea } from './ritual-pasos';
import {
  hitosPorMeta,
  identidadDeArea,
  lecturaDeAnimo,
  logroReciente,
  nivelDeArea,
  puntosDelDia,
  segmentos,
} from './tablero';

const Ritual = lazy(() => import('./ritual').then((m) => ({ default: m.Ritual })));

const conArea = (token: string | undefined): CSSProperties | undefined =>
  token ? ({ ['--area' as string]: `var(${token})` } as CSSProperties) : undefined;

/**
 * LA TARJETA DEL RITUAL. Antes de sellar, los huecos del día —misión, pilar, lo
 * difícil— vacíos y hundidos, esperando a que el ritual los acuñe; sellado, es el
 * tablero del día, con la misión de protagonista. La fecha se CONGELA al abrir: si
 * el día cambia con el ritual abierto, lo escrito va al día en que se empezó.
 */
export function TarjetaRitual({ today, tz }: { today: string; tz: string }): ReactElement | null {
  const settings = useSettings();
  const plan = useDayPlan(today);
  const racha = useRitualStreak(today);
  const areas = useAreaMap();
  const tareas = useTasksOn(today);
  const minutos = useMinutosLocales(tz);
  const [abierto, setAbierto] = useState<string | null>(null);
  // Al sellar desaparece el botón que abrió el ritual: el foco vuelve al título.
  const titulo = useRef<HTMLHeadingElement>(null);

  if (plan === undefined) return null;
  const sellado = plan?.ritualSealedAt != null;
  const destacada = !sellado && ritualDestacado(minutos, settings.dayStartOffsetMin);
  const pilar = plan?.pillarAreaId ? areas.get(plan.pillarAreaId) : undefined;
  const dificil = tareas?.find((t) => t.id === plan?.frogTaskId);
  const dia = `${nombreDelDia(today)} ${fechaLarga(today)}`;

  return (
    <>
      <Card className={`ritual-tarjeta${destacada ? ' ritual-tarjeta--destacada' : ''}`}>
        <header className="ritual-tarjeta__cabecera">
          <div className="ritual-tarjeta__titulo">
            <p className="t-sm t-dim">{dia.charAt(0).toUpperCase() + dia.slice(1)}</p>
            <h2 ref={titulo} tabIndex={-1} className="t-title-2 ritual-tarjeta__h">
              {sellado ? 'Tu día tiene rumbo' : 'Tu ritual de la mañana'}
            </h2>
          </div>
          <div className="ritual-tarjeta__acciones">
            {racha >= 1 ? (
              <span className="ritual-racha t-sm">
                <span className="ritual-moneda" aria-hidden="true">
                  {racha}
                </span>
                <span aria-hidden="true">{racha === 1 ? 'mañana' : 'mañanas'}</span>
                <span className="sr-only">
                  {racha === 1 ? '1 mañana' : `${racha} mañanas seguidas`}
                </span>
              </span>
            ) : null}
            {sellado ? (
              <button
                type="button"
                className="btn btn--bare btn--sm"
                onClick={() => setAbierto(today)}
              >
                Editar
              </button>
            ) : null}
          </div>
        </header>
        {sellado && plan ? (
          <TableroDelDia today={today} plan={plan} pilar={pilar} dificil={dificil} />
        ) : (
          <Invitacion
            today={today}
            plan={plan}
            pilar={pilar}
            dificil={dificil}
            destacada={destacada}
            onEmpezar={() => setAbierto(today)}
          />
        )}
      </Card>
      {abierto !== null ? (
        <Suspense fallback={null}>
          <Ritual
            open
            fecha={abierto}
            tz={tz}
            focoAlCerrar={titulo}
            onClose={() => setAbierto(null)}
          />
        </Suspense>
      ) : null}
    </>
  );
}

/** Un hueco del día: hundido mientras espera, con su valor cuando ya lo tiene. */
function Hueco({
  etiqueta,
  vacio,
  valor,
  mision = false,
}: {
  etiqueta: string;
  vacio?: string;
  valor: ReactNode;
  mision?: boolean;
}): ReactElement {
  const lleno = valor !== null && valor !== undefined && valor !== '';
  return (
    <div
      className={`ritual-hueco${mision ? ' ritual-hueco--mision' : ''}${lleno ? ' ritual-hueco--lleno' : ''}`}
    >
      {lleno ? null : <span className="ritual-hueco__asiento" aria-hidden="true" />}
      <div className="ritual-hueco__texto">
        {mision && !lleno ? (
          <p className="ritual-hueco__titulo">{etiqueta}</p>
        ) : (
          <p className="t-sm t-dim">{etiqueta}</p>
        )}
        {lleno ? (
          <p className={mision ? 'ritual-hueco__titulo' : 'ritual-hueco__valor'}>{valor}</p>
        ) : vacio ? (
          <p className="t-sm t-dim">{vacio}</p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Lo difícil primero, y SE MARCA AQUÍ. Antes era un recuadro que solo se leía: la
 * casilla estaba más abajo en Hoy, debajo del anillo, y desde el ritual parecía que
 * la tarea se quedaba atascada (lo dijo el cliente, 2026-10-30). Es la misma tarea:
 * marcarla aquí o abajo da lo mismo, y los +20 una sola vez.
 */
function HuecoDificil({ tarea, today }: { tarea: Task; today: string }): ReactElement {
  const hecha = tarea.doneAt !== null;
  const reciente = useReciente(hecha);
  return (
    <div
      className={`ritual-hueco ritual-hueco--lleno ritual-hueco--dificil${hecha ? ' ritual-hueco--hecho' : ''}`}
    >
      <div className="ritual-hueco__fila">
        <button
          type="button"
          className="habit__check"
          aria-pressed={hecha}
          aria-label={hecha ? `Desmarcar ${tarea.title}` : `Marcar ${tarea.title} como hecha`}
          data-reciente={reciente}
          onClick={() => void setTaskDone(tarea.id, !hecha, today)}
        >
          <span>{hecha ? <IconCheck size={14} /> : null}</span>
        </button>
        <span className="t-sm tnum ritual-hueco__puntos">
          {hecha ? 'Hecha' : `+${String(POINTS.frogDone)}`}
        </span>
      </div>
      <div className="ritual-hueco__texto">
        <p className="t-sm t-dim">Lo difícil primero</p>
        <p className="ritual-hueco__valor">{tarea.title}</p>
      </div>
    </div>
  );
}

function Invitacion({
  today,
  plan,
  pilar,
  dificil,
  destacada,
  onEmpezar,
}: {
  today: string;
  plan: DayPlan | null;
  pilar: LifeArea | undefined;
  dificil: Task | undefined;
  destacada: boolean;
  onEmpezar: () => void;
}): ReactElement {
  const ayer = useJournal(addDays(today, -1));
  const progression = useProgression();
  const nota = ayer?.entrada ? leerDiario(ayer.entrada.body).manana.trim() : '';
  const mision = plan?.mission.trim() ?? '';
  // Cerrado a medias: lo que ya hay se ve en su hueco y el botón no finge empezar.
  const empezado = mision !== '' || pilar !== undefined || dificil !== undefined;

  return (
    <>
      <div className="ritual-huecos">
        <Hueco
          mision
          etiqueta="Tu misión"
          vacio="Una frase que haga que hoy cuente"
          valor={mision || null}
        />
        <Hueco
          etiqueta="Pilar del día"
          valor={
            pilar ? (
              <span className="ritual-hueco__area" style={conArea(pilar.colorToken)}>
                {pilar.name}
              </span>
            ) : null
          }
        />
        {dificil ? (
          <HuecoDificil tarea={dificil} today={today} />
        ) : (
          <Hueco etiqueta="Lo difícil primero" valor={null} />
        )}
      </div>
      {nota ? (
        <p className="ritual-anoche t-sm">
          <span className="t-body-strong">Anoche escribiste:</span> “{nota}”
        </p>
      ) : null}
      <button
        type="button"
        className={`btn ${destacada ? 'btn--primary' : 'btn--ghost'} btn--block ritual-cta`}
        onClick={onEmpezar}
      >
        {empezado ? 'Seguir con mi ritual' : 'Empezar mi ritual'}
        <span className="ritual-cta__puntos t-sm">+{POINTS.ritualSealed}</span>
      </button>
      {progression ? (
        <p className="ritual-tarjeta__pie t-sm t-dim">
          {puntosDelDia(progression.byDay.get(today) ?? 0, progression.generalLevel)}
        </p>
      ) : null}
    </>
  );
}

/**
 * EL TABLERO DEL DÍA. Una protagonista —la misión, la única pieza de ámbar— y
 * alrededor piezas calladas que dicen de dónde sale el día: el pilar con su nivel,
 * la energía con la que llegas, lo difícil (que aquí se marca) y los proyectos.
 */
function TableroDelDia({
  today,
  plan,
  pilar,
  dificil,
}: {
  today: string;
  plan: DayPlan;
  pilar: LifeArea | undefined;
  dificil: Task | undefined;
}): ReactElement {
  const mood = useMood(today);
  const progression = useProgression();
  const identities = useIdentities();
  const goals = useGoals();
  const hitos = useGoalMilestones();
  const desbloqueos = useMilestoneUnlocks();
  const areas = useAreaMap();

  const proyectos = (goals ?? []).filter((g) => plan.projectGoalIds.includes(g.id));
  const porMeta = hitosPorMeta(hitos);
  const lectura = lecturaDeAnimo(mood);
  const logro = logroReciente(desbloqueos, today);
  const nivel = pilar ? nivelDeArea(progression?.dimensions, pilar.id) : null;
  const identidad = pilar ? identidadDeArea(identities, pilar.id) : null;

  return (
    <>
      <div className="tablero">
        <div className="tablero__pieza tablero__mision">
          <p className="tablero__que t-sm">Misión de hoy</p>
          <p className="tablero__frase">{plan.mission}</p>
          <span className="tablero__sello" aria-hidden="true">
            Sellado
          </span>
        </div>

        {pilar ? (
          <div className="tablero__pieza tablero__pilar" style={conArea(pilar.colorToken)}>
            <p className="t-sm t-dim">Pilar</p>
            <div>
              <p className="tablero__grande">{pilar.name}</p>
              {identidad ? <p className="t-sm t-dim tablero__identidad">{identidad}</p> : null}
            </div>
            {nivel ? (
              <div className="row tablero__nivel">
                <div className="grow">
                  <Bar
                    progress={nivel.progress}
                    color={pilar.colorToken}
                    label={`${pilar.name}: progreso hacia el nivel ${nivel.level + 1}`}
                  />
                </div>
                <span className="t-sm t-dim tnum">Nv {nivel.level}</span>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="tablero__pieza tablero__pieza--hueco">
            <p className="t-sm t-dim">Pilar</p>
            <p className="t-sm t-dim">Sin pilar hoy</p>
          </div>
        )}

        <div className="tablero__pieza">
          <p className="t-sm t-dim">Energía</p>
          <Segmentos
            llenos={segmentos(mood?.energy)}
            label={mood ? `Energía ${mood.energy} de 5` : 'Energía sin registrar'}
          />
          {/* Mientras se lee la base no se dice nada: «Sin registrar» a quien ya lo
              registró es afirmar algo a partir de un hueco en los datos. */}
          {mood === undefined ? null : (
            <p className="t-sm">{lectura ?? <span className="t-dim">Sin registrar</span>}</p>
          )}
        </div>

        {dificil ? <PiezaDificil tarea={dificil} today={today} /> : null}

        {proyectos.length > 0 ? (
          <div className="tablero__pieza tablero__proyectos">
            <p className="t-sm t-dim">Proyectos</p>
            {proyectos.map((g) => {
              const h = porMeta.get(g.id);
              return (
                <span
                  key={g.id}
                  className="chip tablero__chip"
                  style={conArea(areas.get(g.areaId)?.colorToken)}
                >
                  <span className="tablero__punto" aria-hidden="true" />
                  {g.title}
                  {h ? (
                    <span className="tnum">
                      {' '}
                      {h.hechos}/{h.total}
                    </span>
                  ) : null}
                </span>
              );
            })}
          </div>
        ) : null}
      </div>
      {logro ? <p className="ritual-tarjeta__pie t-sm t-dim">Último logro: {logro}</p> : null}
    </>
  );
}

/** Lo difícil primero dentro del tablero: el mismo círculo y el mismo cuajo que una tarea. */
function PiezaDificil({ tarea, today }: { tarea: Task; today: string }): ReactElement {
  const hecha = tarea.doneAt !== null;
  const reciente = useReciente(hecha);
  return (
    <div
      className={`tablero__pieza tablero__dificil tarea tarea--dificil${hecha ? ' tarea--hecha' : ''}`}
    >
      <button
        type="button"
        className="habit__check"
        aria-pressed={hecha}
        aria-label={hecha ? `Desmarcar ${tarea.title}` : `Marcar ${tarea.title} como hecha`}
        data-reciente={reciente}
        onClick={() => void setTaskDone(tarea.id, !hecha, today)}
      >
        <span>{hecha ? <IconCheck size={16} /> : null}</span>
      </button>
      <div className="grow tarea__texto">
        <p className="t-sm t-dim">Lo difícil primero</p>
        <p className="tarea__titulo tablero__grande">{tarea.title}</p>
      </div>
      <span className="tablero__puntos t-sm tnum">+{POINTS.frogDone}</span>
    </div>
  );
}

/**
 * Lo difícil primero, encima de los hábitos: si existe, sigue siendo de hoy y el
 * ritual no está sellado. Sellado, vive en el tablero; aquí sería la misma tarea dos veces.
 */
export function LoDificilPrimero({ today }: { today: string; tz: string }): ReactElement | null {
  const plan = useDayPlan(today);
  const tareas = useTasksOn(today);
  const areas = useAreaMap();
  const tarea = tareas?.find((t) => t.id === plan?.frogTaskId);
  if (!tarea || plan?.ritualSealedAt != null) return null;
  return (
    <section aria-label="Lo difícil primero">
      <p className="t-sm t-dim" style={{ marginBottom: 'var(--sp-2)' }}>
        Lo difícil primero
      </p>
      <ul className="stack">
        <FilaTarea
          titulo={tarea.title}
          hecha={tarea.doneAt !== null}
          dificil
          nota="20 puntos al hacerla"
          color={tarea.areaId ? areas.get(tarea.areaId)?.colorToken : undefined}
          onToggle={() => void setTaskDone(tarea.id, tarea.doneAt === null, today)}
        />
      </ul>
    </section>
  );
}

/** La lista corta de hoy. Lo difícil primero no se repite aquí. */
export function TareasDeHoy({ today, tz }: { today: string; tz: string }): ReactElement {
  const plan = useDayPlan(today);
  const tareas = useTasksOn(today);
  const areas = useAreaMap();
  const [texto, setTexto] = useState('');
  const lista = (tareas ?? []).filter((t) => t.id !== plan?.frogTaskId);
  const dificil = tareas?.find((t) => t.id === plan?.frogTaskId);

  const crear = (event: FormEvent): void => {
    event.preventDefault();
    const titulo = tituloDeTarea(texto);
    if (titulo === null) return;
    setTexto('');
    void addTask({ title: titulo, plannedDate: today, tz });
  };

  const acciones = (t: Task) => [
    ...(puedeSerDificil(t, dificil)
      ? [
          {
            texto: 'Hacer lo difícil primero',
            onSelect: () => void saveDayPlan(today, tz, { frogTaskId: t.id }),
          },
        ]
      : []),
    {
      texto: 'Mover a mañana',
      onSelect: () => void updateTask(t.id, { plannedDate: addDays(today, 1) }),
    },
    { texto: 'Dejar sin día', onSelect: () => void updateTask(t.id, { plannedDate: null }) },
    { texto: 'Borrar', peligro: true, onSelect: () => void deleteTask(t.id) },
  ];

  return (
    <Card>
      <div className="row row--between">
        <p className="t-sm t-dim">Tareas de hoy</p>
        <Link to="/semana" className="t-sm">
          Ver la semana
        </Link>
      </div>
      {lista.length > 0 ? (
        <ul className="stack stack--tight" style={{ marginTop: 'var(--sp-2)' }}>
          {lista.map((t) => (
            <FilaTarea
              key={t.id}
              titulo={t.title}
              hecha={t.doneAt !== null}
              color={t.areaId ? areas.get(t.areaId)?.colorToken : undefined}
              onToggle={() => void setTaskDone(t.id, t.doneAt === null, today)}
              acciones={acciones(t)}
            />
          ))}
        </ul>
      ) : null}
      <form className="row" style={{ marginTop: 'var(--sp-3)' }} onSubmit={crear}>
        <input
          className="input grow"
          value={texto}
          maxLength={140}
          placeholder="Nueva tarea"
          aria-label="Nueva tarea para hoy"
          onChange={(e) => setTexto(e.target.value)}
        />
      </form>
    </Card>
  );
}
